"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  ChevronDown,
  ChevronUp,
  Info,
  AlertTriangle,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import { IZIN_DILIMLERI, hesaplaIzin, type IzinSonuc } from "./izin-engine";

const howToSchema = generateHowToSchema({
  title: "Yıllık İzin Hakkı Nasıl Hesaplanır?",
  description:
    "Türk İş Kanunu 4857'ye göre çalışanın kıdem yılına bağlı yıllık izin hakkını hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "İşe Başlama Tarihini Girin",
      text: "Çalışanın şirkette işe başladığı tarihi seçin.",
    },
    {
      name: "Çalışan Yaşını Girin (Opsiyonel)",
      text: "18 yaş altı veya 50 yaş üstü çalışanlar için ek izin hakkı uygulanır.",
    },
    {
      name: "Sonuçları İnceleyin",
      text: "Kıdem yılı, yasal yıllık izin hakkı ve kıdeme göre ek izin günleri görüntülenir.",
    },
  ],
});

function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

export default function IzinHesaplayiciClient() {
  const [startDate, setStartDate] = useState("");
  const [yas, setYas] = useState("");
  const [sonuc, setSonuc] = useState<IzinSonuc | null>(null);

  const hesapla = useCallback(() => {
    if (!startDate) return;
    const result = hesaplaIzin({
      iseBaslamaTarihi: startDate,
      calIsanYasi: yas ? parseInt(yas) : undefined,
    });
    setSonuc(result);
  }, [startDate, yas]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  return (
    <>
      <StructuredData data={[howToSchema]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-green-500 to-emerald-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "İzin Hesaplayıcı",
                  href: "/araclar/izin-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />
            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Calendar className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  Yıllık İzin Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  İş Kanunu 4857 Madde 53
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Çalışanın kıdem yılına göre yasal yıllık izin hakkını hesaplayın.
              18 yaş altı ve 50 yaş üstü çalışanlar için özel izin hakları.
            </p>
          </div>
        </section>

        {/* Hesaplama */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              {/* İzin Hakkı Tablosu */}
              <div className="mb-8 bg-emerald-50 rounded-xl p-5 border border-emerald-100">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  İzin Hakkı Tablosu (İş Kanunu 4857)
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {IZIN_DILIMLERI.map((d) => (
                    <div
                      key={d.minYil}
                      className="bg-white rounded-lg p-3 text-center"
                    >
                      <span className="text-2xl font-bold text-emerald-600 block">
                        {d.gun}
                      </span>
                      <span className="text-xs text-gray-500">
                        {d.minYil}–{d.maxYil === Infinity ? "∞" : d.maxYil} yıl
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  * 18 yaş altı ve 50 yaş üstü çalışanlarda en az 20 gün
                </p>
              </div>

              {/* Girdiler */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="startDate"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    İşe Başlama Tarihi
                  </label>
                  <input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-900"
                  />
                </div>
                <div>
                  <label
                    htmlFor="yas"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Çalışan Yaşı{" "}
                    <span className="text-gray-400 font-normal">
                      (Opsiyonel)
                    </span>
                  </label>
                  <input
                    id="yas"
                    type="number"
                    inputMode="numeric"
                    min="15"
                    max="70"
                    value={yas}
                    onChange={(e) => setYas(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Yaş"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    18 yaş altı / 50 yaş üstü ek izin hakkı için
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={hesapla}
                className="w-full bg-green-600 text-white py-4 rounded-xl font-semibold hover:bg-green-700 active:bg-green-800 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Hesapla
                </span>
              </button>
            </div>

            {/* Sonuç */}
            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                {!sonuc.izinHakkiVar && sonuc.hataMesaji && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">{sonuc.hataMesaji}</p>
                  </div>
                )}

                {sonuc.izinHakkiVar && (
                  <>
                    {/* Kıdem Bilgisi */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                        <span className="text-xs text-gray-500 block">
                          Kıdem
                        </span>
                        <span className="text-2xl font-bold text-gray-900 block tabular-nums">
                          {sonuc.kidemYili}
                        </span>
                        <span className="text-xs text-gray-500">Yıl</span>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                        <span className="text-xs text-gray-500 block">Ay</span>
                        <span className="text-2xl font-bold text-gray-900 block tabular-nums">
                          {sonuc.kidemAyi}
                        </span>
                        <span className="text-xs text-gray-500">Ay</span>
                      </div>
                      <div className="bg-emerald-600 text-white rounded-xl p-4 text-center">
                        <span className="text-xs text-emerald-100 block">
                          İzin Hakkı
                        </span>
                        <span className="text-3xl font-bold block tabular-nums">
                          {sonuc.ozelYasUygulandiMi
                            ? sonuc.ozelYasIzinGunu
                            : sonuc.izinGunu}
                        </span>
                        <span className="text-xs text-emerald-100">
                          İş Günü
                        </span>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
                        <span className="text-xs text-gray-500 block">
                          Toplam Gün
                        </span>
                        <span className="text-2xl font-bold text-gray-900 block tabular-nums">
                          {sonuc.toplamGun.toLocaleString("tr-TR")}
                        </span>
                        <span className="text-xs text-gray-500">
                          Takvim Günü
                        </span>
                      </div>
                    </div>

                    {/* İzin Dilimi Açıklaması */}
                    <div className="bg-white rounded-xl p-5 border border-gray-200">
                      <span className="text-sm font-semibold text-gray-900">
                        İzin Dilimi
                      </span>
                      <p className="text-sm text-gray-600 mt-1">
                        {sonuc.izinDilimi} → <strong>{sonuc.izinGunu}</strong>{" "}
                        iş günü yasal izin hakkı
                      </p>
                      {sonuc.ozelYasUygulandiMi && (
                        <p className="text-sm text-emerald-600 mt-2 font-medium">
                          ✦ 18 yaş altı / 50 yaş üstü özel kuralı uygulandı →{" "}
                          <strong>{sonuc.ozelYasIzinGunu} iş günü</strong>
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {/* SSS */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Sık Sorulan Sorular
          </h2>
          <div className="space-y-3">
            <SSSSoru
              soru="İzin hakkı ne zaman kazanılır?"
              cevap="Çalışan, işe başladığı tarihten itibaren 1 yıl çalıştıktan sonra yıllık izin hakkı kazanır. İlk yıl içinde izin hakkı kazanılmaz."
            />
            <SSSSoru
              soru="Kullanılmayan izinler ne olur?"
              cevap="Kullanılmayan izinler bir sonraki yıla devredebilir. İşten ayrılma durumunda kullanılmayan izin günleri tazminat olarak ödenir."
            />
            <SSSSoru
              soru="İzin süresi nasıl hesaplanır?"
              cevap="İzin günleri, hafta tatili ve resmi tatiller hariç iş günleri olarak hesaplanır. Örneğin, 20 iş günü izin hakkı olan bir çalışan, hafta sonları ve resmi tatiller hariç 20 gün izin kullanabilir."
            />
            <SSSSoru
              soru="18 yaş altı ve 50 yaş üstü çalışanlar için izin hakkı farkı nedir?"
              cevap="İş Kanunu'na göre 18 yaşından küçük ve 50 yaşından büyük çalışanlar için yıllık izin hakkı en az 20 iş günüdür. Bu kural, kıdem yılına bağlı izin hakkından (14 gün) fazla olduğunda uygulanır."
            />
            <SSSSoru
              soru="İzin talebi nasıl yapılır?"
              cevap="İzin talebi genellikle yazılı olarak veya dijital İK sistemi üzerinden yapılır. IKAI HR ile izin taleplerini online olarak yönetebilir, otomatik onay ve bakiye takibi yapabilirsiniz."
            />
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              İşe Giriş Yılı Hesaplama ile İzin Hakkını Doğru Okuyun
            </h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                <strong>İşe giriş yılı hesaplama</strong> araması yapan ekipler
                aslında tek bir şeyi netleştirmek ister: çalışan bugün
                itibarıyla kaç yıllık kıdeme sahip ve buna göre kaç gün yıllık
                izin hakkı oluştu? Yıllık izin süresi, 4857 sayılı İş Kanunu
                kapsamında tamamlanan kıdem yılına bağlı olduğu için işe başlama
                tarihinin doğru yorumlanması kritik önem taşır.
              </p>
              <p>
                Uygulamada en sık hata, çalışanın yalnızca takvim yılına göre
                değerlendirilmesidir. Oysa önemli olan hangi yılda işe girdiği
                değil, işe başladığı tarihten itibaren kaç tam yıl çalıştığıdır.
                Bir çalışan 1 Ocak&apos;ta değil yılın ortasında işe girdiyse
                izin hakkı da o tarih baz alınarak oluşur. Bu araç, işe giriş
                tarihi ile bugünün tarihi arasındaki kıdem süresini hesaplayarak
                yasal izin hakkını görünür hale getirir.
              </p>
              <p>
                <strong>İşe giriş yılı hesaplama</strong> özellikle küçük ve
                orta ölçekli işletmelerde önemlidir; çünkü izin bakiyesi çoğu
                zaman manuel takip edilir ve hatalı başlangıç tarihi tüm süreci
                bozar. Yanlış kıdem hesabı, eksik ya da fazla izin
                kullandırılmasına, çalışan memnuniyetsizliğine ve işten çıkış
                anında hatalı izin alacağı hesaplarına yol açabilir. Doğru
                hesaplama ise yalnızca İK için değil, yönetici ve çalışan tarafı
                için de daha şeffaf bir süreç oluşturur.
              </p>
              <p>
                Bu sayfadaki hesaplayıcı, işe giriş tarihine göre kıdem yılını,
                yaşa bağlı özel durumları ve o kıdem aralığında geçerli yıllık
                izin süresini birkaç saniyede gösterir. Eğer sonraki adımda izin
                taleplerini, onay akışlarını ve bakiye takibini de
                dijitalleştirmek istiyorsanız, aynı mantığı ürün seviyesinde
                yöneten IKAI izin yönetimi modüllerine geçebilirsiniz.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="p-5 bg-green-50 rounded-xl border border-green-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600">
                Hesaplama, 4857 sayılı İş Kanunu&apos;nun 53. maddesine göre
                yapılmaktadır. İzin günleri iş günü olarak belirtilmiştir.
              </p>
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="izin-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-emerald-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              İzin Yönetimini Otomatikleştirin
            </h2>
            <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
              IKAI ile online izin talepleri, otomatik bakiye hesaplama ve izin
              takvimi.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-emerald-700 px-6 py-3 rounded-xl font-semibold hover:bg-emerald-50 transition-colors"
            >
              Ücretsiz Deneyin
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
